import type { SuggestedProperty } from '@/store/apis/taxDocumentsApi';
import type { PropertyMetadata } from '../../TaxWorkflow/types/tax-process.type';

const ADDRESS_STOPWORDS = new Set([
  'australia',
  'qld',
  'nsw',
  'vic',
  'wa',
  'sa',
  'tas',
  'act',
  'nt',
  'queensland',
  'victoria',
  'wales',
  'south',
  'north',
  'western',
  'new',
]);

export function normalizeAddressTokens(address: string): string[] {
  return address
    .toLowerCase()
    .replace(/[,.\-/]/g, ' ')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 1 && !ADDRESS_STOPWORDS.has(t));
}

export function addressesSimilar(a: string[], b: string[]): boolean {
  if (a.length === 0 || b.length === 0) return false;
  const setA = new Set(a);
  const setB = new Set(b);
  const overlap = [...setA].filter((t) => setB.has(t)).length;
  const shorter = Math.min(setA.size, setB.size);
  return overlap >= 2 && overlap / shorter >= 0.5;
}

/**
 * The properties a document may be filed under, as `{ value, label }` options.
 *
 * Prefers the backend's `assignable` list — linked properties in the deal's tax
 * scope, which is the rule we want. Falls back to the raw registry only when
 * that field is absent, so a frontend deployed ahead of the backend degrades to
 * the previous behaviour instead of offering nothing at all.
 */
export function assignablePropertyOptions(
  folders?: {
    assignable?: { displayName: string }[];
    properties?: { displayName: string }[];
  } | null,
): { value: string; label: string }[] {
  if (!folders) return [];

  const names = folders.assignable
    ? folders.assignable.map((p) => p.displayName)
    : (folders.properties ?? []).map((p) => p.displayName);

  const seen = new Set<string>();
  return names.reduce<{ value: string; label: string }[]>((options, name) => {
    const trimmed = name?.trim();
    if (trimmed && !seen.has(trimmed)) {
      seen.add(trimmed);
      options.push({ value: trimmed, label: trimmed });
    }
    return options;
  }, []);
}

export function filterNewSuggestions(
  suggestions: SuggestedProperty[],
  existing: PropertyMetadata[],
): SuggestedProperty[] {
  const existingTokens = existing
    .map((p) => p.address ?? '')
    .filter(Boolean)
    .map(normalizeAddressTokens);

  return suggestions.filter((s) => {
    const tokens = normalizeAddressTokens(s.address);
    if (tokens.length < 2) return false;
    return !existingTokens.some((et) => addressesSimilar(tokens, et));
  });
}
